import { getProductWithVariants, ProductWithVariants } from '@models/product';
import { ElectronicsB2CLandingComponent } from '../landing-pages/electronics-b2c-landing/electronics-b2c-landing.component';
import { billingAddress, paymentData, submitContract } from '@config/order-step-configs';
import { electronicsB2CDevice, productAdviceHandy } from './steps';
import { documentsWithoutTheftCoverage } from './variants';
import { InsuredDeviceWidgetComponent } from 'app/products/electronic/widgets/insured-device-widget/insured-device-widget.component';
import { ElectronicsB2CInformationWidgetComponent } from 'app/products/electronic/widgets/electronics-b2c-information/electronics-b2c-information-widget.component';
import { PaymentMethodWidgetComponent } from 'app/customer-portal';
import { electronicDamageReportSteps } from './damage-report-steps';
import { getTagsFromProduct, hasTags } from 'app/products/fns';

const copayTaxInfo =
    'Inkl. 19&nbsp;% Versicherungssteuer. Die Selbstbeteiligung beträgt 35&nbsp;€ bei Schäden. Bei Diebstahl beträgt sie 15&nbsp;% vom Kaufpreis.';

const copayTaxInfoDeductibleOnly =
    'Inkl. 19&nbsp;% Versicherungssteuer. Die Selbstbeteiligung beträgt 35&nbsp;€ bei Schäden.';

export const electronicsB2CProduct: ProductWithVariants = getProductWithVariants({
    // base = yearly, with theft coverage
    slug: 'elektronikversicherung',
    title: 'Elektronikversicherung',
    fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung mit Diebstahlschutz',
    collapsableTitle: 'Elektronikversicherung mit Diebstahlschutz',
    insuranceTypeName: 'Elektronikversicherung B2C (J)',
    insuranceTypeId: 29,
    orderConfig: {
        steps: [
            {
                slug: '',
                title: '',
                component: ElectronicsB2CLandingComponent,
            },
            electronicsB2CDevice,
            productAdviceHandy,
            billingAddress,
            paymentData,
            submitContract,
        ],
        taxInfo: 'Inkl. 19 % Versicherungssteuer',
        hasFlexPrice: true,
        requiresMedicalData: false,
        requiresNoPriorDefect: true,
        showDiscountInput: true,
        showPriceFrom: false,
        hideSummarySidebarOnLandingPage: true,
        headerMode: 'default',
        canonical: true,
    },
    portalConfig: {
        icon: 'electronics',
        iconSize: '44px',
        widgets: [
            { component: ElectronicsB2CInformationWidgetComponent },
            { component: PaymentMethodWidgetComponent },
            { component: InsuredDeviceWidgetComponent },
        ],
        damageReportSteps: electronicDamageReportSteps,
    },
    presentation: {
        description: `
            <p class="fw:semibold">
                Unsere Elektronikversicherung schützt nahezu all deine privaten Geräte vor möglichen
                Risiken und Schäden.
            </p>
        `,
        bulletPoints: [
            'Schnelle Schadensabwicklung',
            'Schutz bei Diebstahl (optional)',
            'Tägliche Kündigungsfrist',
            'Keine Mindestlaufzeit',
        ],
        imageUrl: 'assets/img/product-headers/astra_versicherung_elektronikversicherung.jpg',
        imageAlt: 'Du hast die Wahl: Elektrogerät mit und ohne Selbstbeteiligung versichern',
        imageHeadline:
            '<span class="h3 m-0:b">Du hast die Wahl: Elektrogerät mit und ohne Selbstbeteiligung versichern</span>',
        docIPIDUrl: 'astra_IPID_Elektronik_Endkunden_EV_MD_2026.pdf',
        docAVBUrl: 'astra_AVB_Elektronik_Endkunden_EV_MD_2026.pdf',
        docAddInfoUrl: 'astra_Kundeninfo_Elektronik_Endkunden_EV_MD_2026.pdf',
        docAnzeigepflichtUrl: 'Obliegenheiten_und_vorvertragliche_Anzeigepflicht.pdf',
    },
    insuredObjectsForm: {
        headline: 'Zu versicherndes Gerät',
        description: '',
        objectDescriptionSingular: 'Gerät',
        max: 1,
        controls: [
            {
                name: 'type',
                label: 'Art',
                type: 'text',
            },
            {
                name: 'description',
                label: 'Hersteller',
                type: 'text',
            },
            {
                name: 'model',
                label: 'Modell',
                type: 'text',
            },
            {
                name: 'value',
                label: 'Kaufpreis',
                type: 'value',
            },
            {
                name: 'detail',
                label: 'Seriennummer',
                type: 'text',
            },
        ],
    },
    tags: ['hasTheftCoverage'],
    onInit: (state, config) => ({ ...state, tags: new Map([...state.tags, ...getTagsFromProduct(config)]) }),
    variants: [
        // yearly, no theft coverage, no deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-od',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung',
                insuranceTypeName: 'Elektronikversicherung B2C (J) - oD',
                insuranceTypeId: 38,
                presentation: { ...documentsWithoutTheftCoverage },
                tags: [],
            },
        },
        // yearly, no theft coverage, with deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-sb-od',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (J) - oD',
                insuranceTypeId: 40,
                presentation: { ...documentsWithoutTheftCoverage },
                orderConfig: { taxInfo: copayTaxInfoDeductibleOnly },
                tags: ['hasDeductible'],
            },
        },
        // yearly, with theft coverage, with deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-sb',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung und Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (J)',
                insuranceTypeId: 33,
                orderConfig: { taxInfo: copayTaxInfo },
                tags: ['hasDeductible', 'hasTheftCoverage'],
            },
        },
        // yearly, no theft coverage, no deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-od',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung',
                insuranceTypeName: 'Elektronikversicherung B2C (J) - oD 14',
                insuranceTypeId: 46,
                presentation: { ...documentsWithoutTheftCoverage },
                tags: ['boughtInLastTwoWeeks'],
            },
        },
        // yearly, with theft coverage, no deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C (J) 14',
                insuranceTypeId: 42,
                tags: ['boughtInLastTwoWeeks', 'hasTheftCoverage'],
            },
        },
        // yearly, with theft coverage, with deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-sb',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung und Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (J) 14',
                insuranceTypeId: 44,
                orderConfig: { taxInfo: copayTaxInfo },
                tags: ['boughtInLastTwoWeeks', 'hasDeductible', 'hasTheftCoverage'],
            },
        },
        // yearly, no theft coverage, with deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: false,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-sb-od',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (J) - oD 14',
                insuranceTypeId: 48,
                presentation: { ...documentsWithoutTheftCoverage },
                orderConfig: { taxInfo: copayTaxInfoDeductibleOnly },
                tags: ['boughtInLastTwoWeeks', 'hasDeductible'],
            },
        },
        // monthly, no theft coverage, no deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-od',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung',
                insuranceTypeName: 'Elektronikversicherung B2C (M) - oD',
                insuranceTypeId: 39,
                presentation: { ...documentsWithoutTheftCoverage },
                tags: ['hasMonthlySettlementPeriod'],
            },
        },
        // monthly, with theft coverage, no deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-m',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C (M)',
                insuranceTypeId: 32,
                tags: ['hasMonthlySettlementPeriod', 'hasTheftCoverage'],
            },
        },
        // monthly, no theft coverage, with deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-sb-od',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (M) - oD',
                insuranceTypeId: 41,
                presentation: { ...documentsWithoutTheftCoverage },
                orderConfig: { taxInfo: copayTaxInfoDeductibleOnly },
                tags: ['hasMonthlySettlementPeriod', 'hasDeductible'],
            },
        },
        // monthly, with theft coverage, with deductible
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: false,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-sb',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung und Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (M)',
                insuranceTypeId: 34,
                orderConfig: { taxInfo: copayTaxInfo },
                tags: ['hasMonthlySettlementPeriod', 'hasDeductible', 'hasTheftCoverage'],
            },
        },
        // monthly, no theft coverage, no deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-od',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung',
                insuranceTypeName: 'Elektronikversicherung B2C (M) - oD 14',
                insuranceTypeId: 47,
                presentation: { ...documentsWithoutTheftCoverage },
                tags: ['hasMonthlySettlementPeriod', 'boughtInLastTwoWeeks'],
            },
        },
        // monthly, with theft coverage, no deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: false,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-m',
                fullTitle: 'Elektronikversicherung ohne Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C (M) 14',
                insuranceTypeId: 43,
                tags: ['hasMonthlySettlementPeriod', 'hasTheftCoverage', 'boughtInLastTwoWeeks'],
            },
        },
        // monthly, no theft coverage, with deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: false,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-sb-od',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (M) - oD 14',
                insuranceTypeId: 49,
                presentation: { ...documentsWithoutTheftCoverage },
                orderConfig: { taxInfo: copayTaxInfoDeductibleOnly },
                tags: ['hasMonthlySettlementPeriod', 'hasDeductible', 'boughtInLastTwoWeeks'],
            },
        },
        // monthly, with theft coverage, with deductible, new device
        {
            condition: (state) =>
                hasTags(state, {
                    hasTheftCoverage: true,
                    hasDeductible: true,
                    hasMonthlySettlementPeriod: true,
                    boughtInLastTwoWeeks: true,
                }),
            overrides: {
                slug: 'elektronikversicherung-m-sb',
                fullTitle: 'Elektronikversicherung mit Selbstbeteiligung mit Diebstahlschutz',
                collapsableTitle: 'Elektronikversicherung mit Selbstbeteiligung und Diebstahlschutz',
                insuranceTypeName: 'Elektronikversicherung B2C-SB (M) 14',
                insuranceTypeId: 45,
                orderConfig: { taxInfo: copayTaxInfo },
                tags: ['hasMonthlySettlementPeriod', 'hasDeductible', 'hasTheftCoverage', 'boughtInLastTwoWeeks'],
            },
        },
    ],
});
